import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'featured';
  className?: string;
}

export default function ArticleCard({ article, variant = 'default', className = '' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className={`group block bg-background-50 rounded-lg overflow-hidden cursor-pointer ${className}`}
      >
        <div className="relative h-[320px] overflow-hidden">
          <img
            src={article.image}
            alt={article.imageAlt}
            title={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            {article.isExclusive && (
              <span className="inline-block px-2.5 py-1 text-[11px] font-semibold bg-primary-500 text-background-50 rounded-full mb-3">
                独家
              </span>
            )}
            <h3 className="text-lg font-bold text-background-50 leading-snug group-hover:text-primary-300 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <div className="flex items-center gap-4 mt-3 text-xs text-background-50/60">
              <span>{article.date}</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-primary-400"></i>
                {article.heat}热度
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className={`group flex gap-4 p-4 bg-background-50 rounded-lg hover:bg-background-100 transition-colors cursor-pointer ${className}`}
      >
        <div className="w-28 h-20 shrink-0 rounded-md overflow-hidden">
          <img
            src={article.image}
            alt={article.imageAlt}
            title={article.title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-400 text-xs"></i>
              {article.heat}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className={`group block bg-background-50 rounded-lg overflow-hidden cursor-pointer shrink-0 ${className}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={article.image}
          alt={article.imageAlt}
          title={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {article.isExclusive && (
          <span className="absolute top-3 left-3 px-2 py-0.5 text-[10px] font-semibold bg-primary-500 text-background-50 rounded-full">
            独家
          </span>
        )}
        {article.isHot && (
          <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-semibold bg-accent-500 text-background-50 rounded-full">
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="text-[11px] font-medium text-primary-500 uppercase tracking-wide">
          {article.category}
        </span>
        <h3 className="mt-1.5 text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/60">
          <span className="text-[11px] text-foreground-500">{article.date}</span>
          <span className="text-[11px] font-semibold text-primary-500 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-fire-line text-xs"></i>
            {article.heat}
          </span>
        </div>
      </div>
    </Link>
  );
}